import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { verifyGolferAccount } from '@/api/signUp/signUp.actions';
import {
  removeFromLocalStorage,
  setToLocalStorage,
} from '@/services/local.storage.ts';
import { useAuthStore } from '@/stores/useAuthStore.ts';

const VerifyAccountPage: FC = () => {
  const [searchParams] = useSearchParams();
  const { logout } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const verifyAccount = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setErrorMessage(
          'Invalid verification link. Please check your email and try again.'
        );
        return;
      }

      logout();
      setToLocalStorage('token', token);

      try {
        const response = await verifyGolferAccount(token);

        if (response.success) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(
            response.error?.message || 'Verification failed. Please try again.'
          );
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage(
          'An unexpected error occurred. Please try again later.'
        );
      } finally {
        removeFromLocalStorage('token');
      }
    };

    verifyAccount();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4" />
          <p className="text-xl text-gray-700">Verifying your account...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Verification Failed
          </h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <img
              src="/GolfersHonoursLogo.png"
              alt="ProductLogo"
              loading="lazy"
            />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Email Verified!
        </h1>

        <p className="text-gray-600 mb-6">
          Congratulations on registering your account with Golfers Honours! Your
          email has been successfully verified.
        </p>
      </div>
    </div>
  );
};

export default VerifyAccountPage;
